import { User } from "@shopliftr/common-js/shared";
import { jwtDecode } from "jwt-decode";
import { IMgdJwtPayload } from "../interfaces/mgd-jwt-payload";
import { ArrayUtil } from "@shopliftr/common-js/data";
import { InvalidTokenException } from "../../exceptions/invalid-token-exception";

/**
 * Wrapper model for a JWT Authentication token
 *
 * The constructor processes a jwtToken string and populates the model
 *
 * usage `new AuthenticationToken(jwtToken);`
 *
 * @see AuthenticationService
 */
export class AuthenticationToken {

    /**
     * The expiration date for this token
     */
    readonly expiration: Date;

    /**
     * The user associated with this token
     * Not populated on a temporary token
     */
    readonly user: User;

    /**
     * The jwt payload decoded from the jwtToken
     */
    private readonly _decodedToken: IMgdJwtPayload;

    /**
     * Create a AuthenticationToken model
     *
     * jwtToken parameter is required
     *
     * Will Throw an InvalidTokenException if the jwtToken cannot be decoded or is missing required fields
     *
     * @see IMgdJwtPayload
     */
    constructor(

        /**
         * The encoded JWT Token
         */
        public readonly jwtToken: string
    ) {

        if (!jwtToken) {
            throw new InvalidTokenException("JWT toke is undefined");
        }
        try {
            this._decodedToken = jwtDecode<IMgdJwtPayload>(this.jwtToken);
            if (!this._decodedToken?.exp) {
                throw new InvalidTokenException("exp (expiry) field is missing");
            }
            if (!this._decodedToken?.sub) {
                throw new InvalidTokenException("sub (email) field is missing");
            }
            if (ArrayUtil.isEmpty(this._decodedToken?.role)) {
                throw new InvalidTokenException("role field is missing or empty");
            }
            if (ArrayUtil.isEmpty(this._decodedToken?.scope)) {
                throw new InvalidTokenException("scope (permissions) field is missing or empty");
            }

            // Expiry timestamp is in seconds convert to Date.
            this.expiration = new Date(this._decodedToken.exp * 1000);

            if (!this.isTemporary) {
                // Deserialize the user from the jwt token
                this.user = User.deserialize({
                    id: this._decodedToken.id,
                    firstName: this._decodedToken.firstName,
                    lastName: this._decodedToken.lastName,
                    email: this._decodedToken.sub,
                    roles: this._decodedToken.role,
                    permissions: this._decodedToken.scope,
                    teamAccess: this._decodedToken.teamAccess,
                    teamName: this._decodedToken.teamName,
                    teamId: this._decodedToken.teamId,
                    dateCreated: this._decodedToken.dateCreated,
                    dateUpdated: this._decodedToken.dateUpdated,
                    esVersion: this._decodedToken.esVersion,
                });
            }
        }
        catch (error) {
            throw new InvalidTokenException(`Could not decode JWT Token: ${error?.message}`, error);
        }

    }

    /**
     * The value used to populate an http-header
     */
    get headerValue(): string {

        if (!this.jwtToken) {
            return undefined;
        }
        return `Bearer ${this.jwtToken}`;
    }

    /**
     * True if this is an expired token
     *
     * If the token does not have an expiration date it will return expired
     */
    get isExpired(): boolean {

        return this.expiration <= new Date();
    }

    /**
     * True if this is a temporary token used for password resets and user invitations
     */
    get isTemporary(): boolean {

        return this._decodedToken.role?.includes("temporary-token");
    }

    /**
     * Token Time to Live in Milliseconds
     */
    get ttl(): number {

        if (this.isExpired) {
            return 0;
        }
        return this.expiration.getTime() - Date.now();
    }

    /**
     * The email associated with the token
     */
    get email(): string {

        return this._decodedToken.sub;
    }
}
